import type { AnyRecord, IncidentRecord, RecordDetail, SourceRecord } from '@fruuit/shared'
import {
  Button,
  Callout,
  Card,
  Dialog,
  Drawer,
  EmptyState,
  PageHeader,
  SkeletonText,
  StatusBadge,
  useToast,
} from '@fruuit/ui'
import { FilePlus2, GitCompareArrows, Link2, Plus, Send } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { errorMessage, isApiError } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { useTitle } from '../../lib/useTitle'
import { blockedMessage, type ReviewDialogAction } from '../review/copy'
import { ReviewActionDialog } from '../review/ReviewActionDialog'
import { recordsApi, useInvalidateRecords, useRecord, useRecordList } from './api'
import { CorrectionDialog } from './CorrectionDialog'
import { CreateIncidentDialog, CreateSourceDialog } from './dialogs'
import {
  createPayload,
  type FieldErrors,
  type FieldSpec,
  FormAlert,
  type FormValues,
  initialValues,
  RecordFields,
  serverErrors,
  updatePayload,
  validateRecord,
} from './form'
import { formatDate, todayIso } from './format'
import { HistoryPanel } from './HistoryPanel'
import { EDITABLE_STATES, editableExplanation } from './labels'
import { ASSESSMENT_FIELDS } from './specs'
import { VersionDiff } from './VersionDiff'

const EVIDENCE_FIELDS: readonly FieldSpec[] = [
  {
    name: 'incident_ids',
    label: 'Incidents',
    type: 'refs',
    refKind: 'incidents',
    hint: 'Incidents must concern the same company. A substantiated concern needs at least one.',
    filter: (values) => (values.issuer_id ? { issuer_id: String(values.issuer_id) } : {}),
  },
  {
    name: 'source_ids',
    label: 'Evidence sources',
    type: 'refs',
    refKind: 'sources',
    hint: 'Sources must concern the same company or no company.',
    filter: (values) => (values.issuer_id ? { issuer_id: String(values.issuer_id) } : {}),
  },
]

const ALL_FIELDS = [...ASSESSMENT_FIELDS, ...EVIDENCE_FIELDS]
const LOCKED = ['issuer_id', 'client_org_id']
const REVIEWER_ACTIONS = ['approve', 'publish', 'return', 'retract'] as const
const SHORT_LABELS = {
  approve: 'Approve',
  publish: 'Publish',
  return: 'Return for changes',
  retract: 'Retract',
} as const

const NEW_DEFAULTS: FormValues = {
  priority: 'Routine monitoring',
  effective_date: todayIso(),
  change_reason: 'Initial assessment',
}

export interface AssessmentWorkbenchProps {
  /** Omit for a new assessment. */
  id?: string
  /** Pre-select a company for a new assessment (`?issuer=`). */
  issuerId?: string | null
}

/** S13 Assessment workbench (R-06): author, evidence, submit, history and corrections. */
export function AssessmentWorkbench({ id, issuerId }: AssessmentWorkbenchProps) {
  const detail = useRecord('assessments', id)
  if (!id) return <WorkbenchForm record={null} issuerId={issuerId ?? null} />
  if (detail.isError) {
    return (
      <>
        <PageHeader
          eyebrow="Fruuit research"
          title="Assessment unavailable"
          breadcrumbs={[
            { label: 'Assessments', href: '/workspace/assessments' },
            { label: 'Not found' },
          ]}
        />
        <EmptyState
          title={
            isApiError(detail.error, 'not_found')
              ? 'This assessment does not exist'
              : 'The assessment could not be loaded'
          }
          description={errorMessage(detail.error)}
        />
      </>
    )
  }
  if (!detail.data) {
    return (
      <div aria-busy="true">
        <SkeletonText lines={8} />
      </div>
    )
  }
  return (
    <WorkbenchForm
      key={`${detail.data.id}:${detail.data.revision}`}
      record={detail.data}
      issuerId={null}
    />
  )
}

type Panel = 'incidents' | 'sources' | null

function WorkbenchForm({
  record,
  issuerId,
}: {
  record: RecordDetail<'assessments'> | null
  issuerId: string | null
}) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const invalidate = useInvalidateRecords()
  const editable = !record || EDITABLE_STATES.includes(record.state)
  const canEdit = !record || record.actions.includes('edit')
  const initial = useMemo(
    () =>
      initialValues(
        ALL_FIELDS,
        record as unknown as Record<string, unknown> | null,
        issuerId ? { ...NEW_DEFAULTS, issuer_id: issuerId } : NEW_DEFAULTS,
      ),
    [record, issuerId],
  )
  const [values, setValues] = useState<FormValues>(initial)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [panel, setPanel] = useState<Panel>(null)
  const [creating, setCreating] = useState<'incident' | 'source' | null>(null)
  const [reviewAction, setReviewAction] = useState<ReviewDialogAction | null>(null)
  const [correcting, setCorrecting] = useState(false)
  const [compare, setCompare] = useState<{ currentId: string; previousId: string } | null>(null)

  const issuerName = record?.issuer_name ?? null
  useTitle(
    record ? `${issuerName ?? 'Assessment'} v${record.version}` : 'New assessment',
    'Assessments',
  )

  const dirty = useMemo(
    () => Object.keys(updatePayload(ALL_FIELDS, values, initial)).length > 0,
    [values, initial],
  )

  const save = useApiMutation(
    (body: FormValues, ctx) =>
      record
        ? recordsApi.update('assessments', record.id, body, ctx)
        : recordsApi.create('assessments', body, ctx),
    {
      onSuccess: async (saved) => {
        await invalidate()
        toast({
          tone: 'success',
          message: record
            ? 'Draft saved. Nothing is visible to clients until publication.'
            : 'Draft assessment created. Nothing is visible to clients until publication.',
        })
        if (!record) navigate(`/workspace/assessments/${saved.id}`, { replace: true })
      },
      onError: (error) => {
        const parsed = serverErrors(error)
        setErrors(parsed.fields)
        setFormError(parsed.form)
      },
    },
  )

  function change(name: string, value: unknown) {
    setValues((current) => ({ ...current, [name]: value }))
    if (errors[name]) setErrors(({ [name]: _removed, ...rest }) => rest)
  }

  function submitForm() {
    const payload = createPayload(ALL_FIELDS, values)
    const check = validateRecord('assessments', payload)
    setErrors(check.fields)
    setFormError(check.form)
    if (Object.keys(check.fields).length > 0 || check.form) {
      setFormError(check.form ?? 'Some fields need attention before this draft can be saved.')
      return
    }
    if (record) {
      const changes = updatePayload(ALL_FIELDS, values, initial)
      for (const name of LOCKED) delete changes[name]
      if (Object.keys(changes).length === 0) return
      save.mutate({ revision: record.revision, ...changes })
    } else {
      save.mutate(payload)
    }
  }

  function link(field: 'incident_ids' | 'source_ids', recordId: string) {
    const current = Array.isArray(values[field]) ? (values[field] as string[]) : []
    if (!current.includes(recordId)) change(field, [...current, recordId])
  }

  function unlink(field: 'incident_ids' | 'source_ids', recordId: string) {
    const current = Array.isArray(values[field]) ? (values[field] as string[]) : []
    change(
      field,
      current.filter((other) => other !== recordId),
    )
  }

  const blocked = blockedMessage(record?.blocked_reason)
  const issuer = typeof values.issuer_id === 'string' ? values.issuer_id : ''
  const client = typeof values.client_org_id === 'string' ? values.client_org_id : ''
  const disabled = !editable || !canEdit || save.isPending

  const title = record
    ? `${issuerName ?? 'Assessment'} · version ${record.version}`
    : 'New assessment'

  return (
    <>
      <PageHeader
        eyebrow="Fruuit research"
        title={title}
        description={
          record
            ? 'Retrieval signals, incidents and assessments remain distinct. Company responses do not replace Fruuit’s judgment.'
            : 'Record a conclusion with its norms, confidence and evidence. It stays a draft until an independent reviewer publishes it.'
        }
        breadcrumbs={[
          { label: 'Assessments', href: '/workspace/assessments' },
          { label: record ? `${issuerName ?? 'Assessment'} v${record.version}` : 'New' },
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {record ? <StatusBadge status={record.state} /> : null}
            {record?.actions.includes('correct') ? (
              <Button variant="secondary" icon={<FilePlus2 />} onClick={() => setCorrecting(true)}>
                Correct (new version)
              </Button>
            ) : null}
            {record?.previous_id ? (
              <Button
                variant="secondary"
                icon={<GitCompareArrows />}
                onClick={() =>
                  setCompare({ currentId: record.id, previousId: record.previous_id as string })
                }
              >
                Compare
              </Button>
            ) : null}
            {record?.actions.includes('submit') ? (
              <Button
                variant="secondary"
                icon={<Send />}
                disabled={dirty}
                title={dirty ? 'Save your changes before submitting.' : undefined}
                onClick={() => setReviewAction('submit')}
              >
                Submit for review
              </Button>
            ) : null}
            {REVIEWER_ACTIONS.filter((next) => record?.actions.includes(next)).map((next) => (
              <Button
                key={next}
                variant={next === 'return' || next === 'retract' ? 'secondary' : 'primary'}
                onClick={() => setReviewAction(next)}
              >
                {SHORT_LABELS[next]}
              </Button>
            ))}
            {editable && canEdit ? (
              <Button
                onClick={submitForm}
                loading={save.isPending}
                disabled={record ? !dirty : false}
              >
                {record ? 'Save draft' : 'Create draft'}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-w-0 flex-col gap-6">
          {record ? (
            <Callout tone={editable ? 'note' : 'info'} title={editable ? undefined : 'Locked'}>
              {editableExplanation(record.state)}
              {record.state === 'returned' && record.return_reason ? (
                <span className="mt-1 block">
                  <strong>Reviewer:</strong> {record.return_reason}
                </span>
              ) : null}
            </Callout>
          ) : null}
          {blocked && record && ['in_review', 'approved'].includes(record.state) ? (
            <Callout tone="warning" title="Independent review required">
              {blocked}
            </Callout>
          ) : null}
          {dirty && record ? (
            <p className="text-sm text-watch" role="status">
              You have unsaved changes.
            </p>
          ) : null}
          <FormAlert message={formError} />
          <Card title="Conclusion" eyebrow="Assessment">
            <form
              noValidate
              onSubmit={(event) => {
                event.preventDefault()
                submitForm()
              }}
            >
              <RecordFields
                kind="assessments"
                fields={ASSESSMENT_FIELDS}
                values={values}
                errors={errors}
                onChange={change}
                disabled={disabled}
                locked={record ? LOCKED : []}
              />
            </form>
          </Card>
          <Card
            title="Evidence"
            eyebrow="Incidents and sources"
            actions={
              editable && canEdit ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<Link2 />}
                    disabled={!issuer}
                    onClick={() => setPanel('incidents')}
                  >
                    Incident register
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<Link2 />}
                    disabled={!issuer}
                    onClick={() => setPanel('sources')}
                  >
                    Evidence sources
                  </Button>
                </div>
              ) : null
            }
          >
            {!issuer ? (
              <p className="mb-4 text-sm text-sage">
                Choose the company first to link its evidence.
              </p>
            ) : null}
            <RecordFields
              kind="assessments"
              fields={EVIDENCE_FIELDS}
              values={values}
              errors={errors}
              onChange={change}
              disabled={disabled || !issuer}
            />
          </Card>
        </div>

        <aside className="flex min-w-0 flex-col gap-6">
          {record ? (
            <>
              <Card title="Version history" padding="md">
                <HistoryPanel
                  kind="assessments"
                  id={record.id}
                  currentId={record.id}
                  onOpenVersion={(other) => navigate(`/workspace/assessments/${other}`)}
                  onCompare={(currentId, previousId) => setCompare({ currentId, previousId })}
                />
              </Card>
              <Card title="Contributors" padding="md">
                {record.contributors.length === 0 ? (
                  <p className="text-sm text-sage">No edits recorded yet.</p>
                ) : (
                  <ul className="flex flex-col gap-2 text-sm">
                    {record.contributors.map((person) => (
                      <li key={person.user_id} className="flex justify-between gap-3">
                        <span className="font-medium text-ink">{person.display_name}</span>
                        <span className="text-sage">
                          {person.edit_count} {person.edit_count === 1 ? 'edit' : 'edits'} · last{' '}
                          {formatDate(person.last_edited_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-3 text-xs leading-relaxed text-sage">
                  Anyone who edited this draft cannot approve or publish it.
                </p>
              </Card>
            </>
          ) : (
            <Card title="How release works" variant="tint" padding="md">
              <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-sm text-ink-soft">
                <li>Create the draft and link its evidence.</li>
                <li>Submit it for review.</li>
                <li>A reviewer who did not contribute approves it.</li>
                <li>Publication makes the version visible to clients. It is then immutable.</li>
              </ol>
            </Card>
          )}
        </aside>
      </div>

      <EvidencePanel
        open={panel !== null}
        kind={panel ?? 'incidents'}
        issuerId={issuer}
        linked={
          (panel === 'sources' ? values.source_ids : values.incident_ids) as string[] | undefined
        }
        onClose={() => setPanel(null)}
        onToggle={(recordId, linked) => {
          const field = panel === 'sources' ? 'source_ids' : 'incident_ids'
          if (linked) unlink(field, recordId)
          else link(field, recordId)
        }}
        onCreate={() => setCreating(panel === 'sources' ? 'source' : 'incident')}
      />
      <CreateIncidentDialog
        open={creating === 'incident'}
        onClose={() => setCreating(null)}
        issuerId={issuer || null}
        clientOrgId={client || null}
        onSaved={(incident: IncidentRecord) => link('incident_ids', incident.id)}
      />
      <CreateSourceDialog
        open={creating === 'source'}
        onClose={() => setCreating(null)}
        issuerId={issuer || null}
        clientOrgId={client || null}
        onSaved={(source: SourceRecord) => link('source_ids', source.id)}
      />
      <ReviewActionDialog
        action={reviewAction}
        target={
          record
            ? {
                kind: 'assessments',
                id: record.id,
                revision: record.revision,
                title: `${issuerName ?? 'Assessment'} · version ${record.version}`,
              }
            : null
        }
        onClose={() => setReviewAction(null)}
      />
      {record ? (
        <CorrectionDialog
          open={correcting}
          kind="assessments"
          recordId={record.id}
          version={record.version}
          onClose={() => setCorrecting(false)}
          onCorrected={(next: AnyRecord) => navigate(`/workspace/assessments/${next.id}`)}
        />
      ) : null}
      <Dialog
        open={compare !== null}
        onClose={() => setCompare(null)}
        size="lg"
        title="Compare versions"
        description="What the correction changes, field by field."
      >
        {compare ? (
          <VersionDiff
            kind="assessments"
            previousId={compare.previousId}
            currentId={compare.currentId}
          />
        ) : null}
      </Dialog>
    </>
  )
}

/** Side panel listing the company's incidents or sources, to link or create (S13). */
function EvidencePanel({
  open,
  kind,
  issuerId,
  linked = [],
  onClose,
  onToggle,
  onCreate,
}: {
  open: boolean
  kind: 'incidents' | 'sources'
  issuerId: string
  linked: string[] | undefined
  onClose: () => void
  onToggle: (id: string, linked: boolean) => void
  onCreate: () => void
}) {
  const list = useRecordList(
    kind,
    { issuer_id: issuerId, limit: 50 },
    { enabled: open && Boolean(issuerId) },
  )
  const items = (list.data?.items ?? []) as (IncidentRecord | SourceRecord)[]
  const isIncidents = kind === 'incidents'
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isIncidents ? 'Incident register' : 'Evidence sources'}
      className="w-[min(30rem,100vw)]!"
      footer={
        <Button fullWidth icon={<Plus />} onClick={onCreate}>
          {isIncidents ? 'Record a new incident' : 'Add a new source'}
        </Button>
      }
    >
      <div className="px-5 py-4">
        <p className="mb-4 text-sm leading-relaxed text-sage">
          {isIncidents
            ? 'Incidents for this company. Status changes are separate records linked to the earlier status.'
            : 'Provenance and sharing rights. No full-text articles are stored.'}
        </p>
        {list.isLoading ? (
          <SkeletonText lines={5} />
        ) : list.isError ? (
          <p className="text-sm text-danger">{errorMessage(list.error)}</p>
        ) : items.length === 0 ? (
          <EmptyState
            variant="plain"
            title={isIncidents ? 'No incidents recorded yet' : 'No sources recorded yet'}
            description="Add the first record to start this workflow."
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map((item) => {
              const isLinked = linked.includes(item.id)
              return (
                <li key={item.id} className="rounded-md border border-line p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink">{item.title}</p>
                      <p className="mt-0.5 text-xs text-sage">
                        {item.kind === 'incidents'
                          ? `${item.legal_status} · ${item.event_date ? formatDate(item.event_date) : 'date unverified'}`
                          : `${item.source_class} · ${item.sharing === 'client' ? 'Shareable' : 'Internal'} · retrieved ${formatDate(item.retrieved_at)}`}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant={isLinked ? 'secondary' : 'primary'}
                      onClick={() => onToggle(item.id, isLinked)}
                      aria-pressed={isLinked}
                    >
                      {isLinked ? 'Unlink' : 'Link'}
                    </Button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </Drawer>
  )
}
